import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { STRING_CONFIRM_APP_QUIT_DURING_PASSCODE_CHANGE, STRING_CONFIRM_APP_QUIT_DURING_PASSCODE_REMOVAL, STRING_E2E_ENABLED, STRING_ENC_NOT_ENABLED, STRING_LOCAL_ENC_ENABLED, STRING_NON_MATCHING_PASSCODES, StringUtils, Strings, } from '@/Constants/Strings';
import { preventRefreshing } from '@/Utils';
import { alertDialog } from '@standardnotes/ui-services';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApplicationEvent } from '@standardnotes/snjs';
import { observer } from 'mobx-react-lite';
import { Title, Text } from '@/Components/Preferences/PreferencesComponents/Content';
import Button from '@/Components/Button/Button';
import PreferencesGroup from '../../PreferencesComponents/PreferencesGroup';
import PreferencesSegment from '../../PreferencesComponents/PreferencesSegment';
import DecoratedPasswordInput from '@/Components/Input/DecoratedPasswordInput';
import { classNames } from '@standardnotes/utils';
import { c } from 'ttag';
const PasscodeLock = ({ application }) => {
    const isNativeMobileWeb = application.isNativeMobileWeb();
    const keyStorageInfo = StringUtils.keyStorageInfo(application);
    const { setIsEncryptionEnabled, setIsBackupEncrypted, setEncryptionStatusString } = application.accountMenuController;
    const passcodeInputRef = useRef(null);
    const [passcode, setPasscode] = useState();
    const [passcodeConfirmation, setPasscodeConfirmation] = useState();
    const [selectedAutoLockInterval, setSelectedAutoLockInterval] = useState(null);
    const [isPasscodeFocused, setIsPasscodeFocused] = useState(false);
    const [showPasscodeForm, setShowPasscodeForm] = useState(false);
    const [canAddPasscode, setCanAddPasscode] = useState(!application.isEphemeralSession());
    const [hasPasscode, setHasPasscode] = useState(application.hasPasscode());
    const [mobilePasscodeTimingOptions, setMobilePasscodeTimingOptions] = useState(() => application.protections.getMobilePasscodeTimingOptions());
    const handleAddPassCode = () => {
        setShowPasscodeForm(true);
        setIsPasscodeFocused(true);
    };
    const changePasscodePressed = () => {
        handleAddPassCode();
    };
    const reloadDesktopAutoLockInterval = useCallback(async () => {
        var _a;
        const interval = await ((_a = application.autolockService) === null || _a === void 0 ? void 0 : _a.getAutoLockInterval());
        setSelectedAutoLockInterval(interval);
    }, [application]);
    const refreshEncryptionStatus = useCallback(() => {
        const hasUser = application.hasAccount();
        const hasPasscode = application.hasPasscode();
        setHasPasscode(hasPasscode);
        const encryptionEnabled = hasUser || hasPasscode;
        const encryptionStatusString = hasUser
            ? STRING_E2E_ENABLED()
            : hasPasscode
                ? STRING_LOCAL_ENC_ENABLED()
                : STRING_ENC_NOT_ENABLED();
        setEncryptionStatusString(encryptionStatusString);
        setIsEncryptionEnabled(encryptionEnabled);
        setIsBackupEncrypted(encryptionEnabled);
    }, [application, setEncryptionStatusString, setIsBackupEncrypted, setIsEncryptionEnabled]);
    const selectDesktopAutoLockInterval = async (interval) => {
        var _a;
        if (!(await application.authorizeAutolockIntervalChange())) {
            return;
        }
        await ((_a = application.autolockService) === null || _a === void 0 ? void 0 : _a.setAutoLockInterval(interval));
        reloadDesktopAutoLockInterval().catch(console.error);
    };
    const setMobilePasscodeTiming = (timing) => {
        application.protections.setMobilePasscodeTiming(timing);
        setMobilePasscodeTimingOptions(application.protections.getMobilePasscodeTimingOptions());
    };
    const removePasscodePressed = async () => {
        await preventRefreshing(STRING_CONFIRM_APP_QUIT_DURING_PASSCODE_REMOVAL(), async () => {
            var _a;
            if (await application.removePasscode()) {
                if (!isNativeMobileWeb) {
                    await ((_a = application.autolockService) === null || _a === void 0 ? void 0 : _a.deleteAutolockPreference());
                    await reloadDesktopAutoLockInterval();
                }
                refreshEncryptionStatus();
            }
        });
    };
    const handlePasscodeChange = (value) => {
        setPasscode(value);
    };
    const handleConfirmPasscodeChange = (value) => {
        setPasscodeConfirmation(value);
    };
    const submitPasscodeForm = async (event) => {
        event.preventDefault();
        if (!passcode || passcode.length === 0) {
            await alertDialog({
                text: Strings.enterPasscode(),
            });
            setIsPasscodeFocused(true);
            return;
        }
        if (passcode !== passcodeConfirmation) {
            await alertDialog({
                text: STRING_NON_MATCHING_PASSCODES(),
            });
            setIsPasscodeFocused(true);
            return;
        }
        await preventRefreshing(STRING_CONFIRM_APP_QUIT_DURING_PASSCODE_CHANGE(), async () => {
            const successful = application.hasPasscode()
                ? await application.changePasscode(passcode)
                : await application.addPasscode(passcode);
            if (!successful) {
                setIsPasscodeFocused(true);
            }
        });
        setPasscode(undefined);
        setPasscodeConfirmation(undefined);
        setShowPasscodeForm(false);
        refreshEncryptionStatus();
    };
    useEffect(() => {
        refreshEncryptionStatus();
    }, [refreshEncryptionStatus]);
    useEffect(() => {
        if (!isNativeMobileWeb) {
            reloadDesktopAutoLockInterval().catch(console.error);
        }
    }, [reloadDesktopAutoLockInterval, isNativeMobileWeb]);
    useEffect(() => {
        var _a;
        if (isPasscodeFocused) {
            (_a = passcodeInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setIsPasscodeFocused(false);
        }
    }, [isPasscodeFocused]);
    useEffect(() => {
        const removeKeyStatusChangedObserver = application.addEventObserver(async () => {
            setCanAddPasscode(!application.isEphemeralSession());
            setHasPasscode(application.hasPasscode());
            setShowPasscodeForm(false);
        }, ApplicationEvent.KeyStatusChanged);
        return () => {
            removeKeyStatusChangedObserver();
        };
    }, [application]);
    const cancelPasscodeForm = () => {
        setShowPasscodeForm(false);
        setPasscode(undefined);
        setPasscodeConfirmation(undefined);
    };
    const autolockService = application.autolockService;
    return (_jsxs(_Fragment, { children: [_jsx(PreferencesGroup, { children: _jsxs(PreferencesSegment, { children: [_jsx(Title, { children: c('B6.Preferences.Security.Title').t `Passcode lock` }), !hasPasscode && canAddPasscode && (_jsxs(_Fragment, { children: [_jsx(Text, { className: "mb-3", children: c('B6.Preferences.Security.Info')
                                        .t `Add a passcode to lock the application and encrypt on-device key storage.` }), keyStorageInfo && _jsx(Text, { className: "mb-3", children: keyStorageInfo }), !showPasscodeForm && (_jsx(Button, { label: c('B6.Preferences.Security.Action').t `Add passcode`, onClick: handleAddPassCode, primary: true }))] })), !hasPasscode && !canAddPasscode && (_jsx(Text, { children: c('B6.Preferences.Security.Warning')
                                .t `Adding a passcode is not supported in temporary sessions. Please sign out, then sign back in with the "Stay signed in" option checked.` })), showPasscodeForm && (_jsxs("form", { className: "sk-panel-form", onSubmit: submitPasscodeForm, children: [_jsx(DecoratedPasswordInput, { type: "password", ref: passcodeInputRef, value: passcode ? passcode : '', onChange: handlePasscodeChange, placeholder: c('B6.Preferences.Security.Placeholder').t `Passcode` }), _jsx(DecoratedPasswordInput, { className: { container: 'mt-2' }, type: "password", value: passcodeConfirmation ? passcodeConfirmation : '', onChange: handleConfirmPasscodeChange, placeholder: c('B6.Preferences.Security.Placeholder').t `Confirm Passcode` }), _jsx(Button, { primary: true, onClick: submitPasscodeForm, label: c('B6.Preferences.Security.Action').t `Set Passcode`, className: "mr-3 mt-3" }), _jsx(Button, { onClick: cancelPasscodeForm, label: c('B6.Preferences.Security.Action').t `Cancel` })] })), hasPasscode && !showPasscodeForm && (_jsxs(_Fragment, { children: [_jsx(Text, { children: c('B6.Preferences.Security.Info').t `Passcode lock is enabled.` }), _jsxs("div", { className: "mt-3 flex flex-row", children: [_jsx(Button, { label: c('B6.Preferences.Security.Action').t `Change Passcode`, onClick: changePasscodePressed, className: "mr-3" }), _jsx(Button, { colorStyle: "danger", label: c('B6.Preferences.Security.Action').t `Remove Passcode`, onClick: removePasscodePressed })] })] }))] }) }), hasPasscode && autolockService && (_jsxs(_Fragment, { children: [_jsx("div", { className: "min-h-3" }), _jsx(PreferencesGroup, { children: _jsxs(PreferencesSegment, { children: [_jsx(Title, { children: c('B6.Preferences.Security.Title').t `Autolock` }), _jsx(Text, { className: "mb-3", children: c('B6.Preferences.Security.Info').t `The autolock timer begins when the window or tab loses focus.` }), _jsx("div", { className: "flex flex-row items-center", children: autolockService.getAutoLockIntervalOptions().map((option) => {
                                        return (_jsx("a", { className: classNames('mr-3 cursor-pointer rounded', option.value === selectedAutoLockInterval
                                                ? 'bg-info px-1.5 py-0.5 text-info-contrast'
                                                : 'text-info'), onClick: () => selectDesktopAutoLockInterval(option.value), children: option.label }, option.value));
                                    }) })] }) })] })), hasPasscode && isNativeMobileWeb && (_jsxs(_Fragment, { children: [_jsx("div", { className: "min-h-3" }), _jsx(PreferencesGroup, { children: _jsxs(PreferencesSegment, { children: [_jsx(Title, { children: c('B6.Preferences.Security.Title').t `Passcode Autolock` }), _jsx("div", { className: "flex flex-row items-center", children: _jsxs("div", { className: "mt-2 flex flex-row items-center", children: [_jsx("div", { className: 'mr-3', children: c('B6.Preferences.Security.Label').t `Require Passcode` }), mobilePasscodeTimingOptions.map((option) => {
                                                return (_jsx("a", { className: classNames('mr-3 cursor-pointer rounded px-1.5 py-0.5', option.selected ? 'bg-info text-info-contrast' : 'text-info'), onClick: () => {
                                                        void setMobilePasscodeTiming(option.key);
                                                    }, children: option.title }, option.key));
                                            })] }) })] }) })] }))] }));
};
export default observer(PasscodeLock);
